/**
 * Trovva — Studio
 *
 * The redesigned AI writing surface. Organized by AUDIENCE (Listings /
 * Buyers / Sellers / Past Clients) instead of by output type (copy /
 * social / email). Agents don't think "I need a listing thing" — they
 * think "I'm meeting a seller tomorrow."
 *
 * Layout:
 *   1. Category strip (4 tabs, emoji + label)
 *   2. Tool grid (cards for each tool in the selected category)
 *   3. Click a tool → inline form → Generate → output panel with
 *      Copy / Regenerate / Close actions
 */
import { useState, useMemo } from 'react';
import {
  Sparkles, Home, Share2, FileText, Video, Mail, CheckSquare, MapPin,
  TrendingUp, BarChart3, ClipboardList, Gift, RefreshCw, Users,
  ArrowLeft, Copy, CheckCircle2, Loader2, Wand2, X,
} from 'lucide-react';
import { theme } from '../../lib/theme';
import { supabase } from '../../lib/supabase';
import { CATEGORIES, TOOLS, toolsByCategory } from '../../lib/studioTemplates';

const ICON = {
  Home, Share2, FileText, Video, Mail, CheckSquare, MapPin,
  TrendingUp, BarChart3, ClipboardList, Gift, RefreshCw, Users,
};

export default function StudioView({ demoMode, showToast }) {
  const [category, setCategory] = useState('listings');
  const [openTool, setOpenTool] = useState(null);

  return (
    <div style={{ padding: '20px 22px', maxWidth: 1100, margin: '0 auto' }}>
      {/* Hero */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <div style={{
          width: 44, height: 44, borderRadius: 12,
          background: `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.primaryDark})`,
          color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Wand2 size={22} />
        </div>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, color: theme.colors.text }}>Studio</h1>
          <div style={{ fontSize: 13, color: theme.colors.textLight }}>
            AI that drafts for you — organized by who you're writing to.
          </div>
        </div>
      </div>

      {openTool ? (
        <ToolWorkspace
          tool={openTool}
          demoMode={demoMode}
          showToast={showToast}
          onBack={() => setOpenTool(null)}
        />
      ) : (
        <>
          {/* Category tabs */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            {CATEGORIES.map(c => (
              <button
                key={c.key}
                onClick={() => setCategory(c.key)}
                style={{
                  flex: '1 1 180px', minWidth: 180,
                  padding: '12px 14px', textAlign: 'left',
                  border: `2px solid ${category === c.key ? theme.colors.primary : theme.colors.border}`,
                  background: category === c.key ? '#F0FDFA' : theme.colors.card,
                  borderRadius: 12, cursor: 'pointer',
                }}
              >
                <div style={{ fontSize: 20 }}>{c.emoji}</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: theme.colors.text, marginTop: 6 }}>{c.label}</div>
                <div style={{ fontSize: 11, color: theme.colors.textLight, marginTop: 3, lineHeight: 1.4 }}>{c.blurb}</div>
              </button>
            ))}
          </div>

          {/* Tool grid for the selected category */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 10 }}>
            {toolsByCategory(category).map(t => {
              const Icon = ICON[t.icon] || Sparkles;
              return (
                <button
                  key={t.key}
                  onClick={() => setOpenTool(t)}
                  style={{
                    textAlign: 'left', cursor: 'pointer',
                    background: theme.colors.card,
                    border: `1px solid ${theme.colors.border}`,
                    borderRadius: 12, padding: 14,
                    display: 'flex', gap: 12, alignItems: 'flex-start',
                  }}
                >
                  <div style={{
                    width: 36, height: 36, borderRadius: 10,
                    background: `${theme.colors.primary}15`, color: theme.colors.primary,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <Icon size={18} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: theme.colors.text }}>{t.label}</div>
                    <div style={{ fontSize: 12, color: theme.colors.textLight, marginTop: 3, lineHeight: 1.4 }}>{t.blurb}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// ToolWorkspace — inline form + generate + output pane
// ──────────────────────────────────────────────────────────────────
function ToolWorkspace({ tool, demoMode, showToast, onBack }) {
  const [formData, setFormData] = useState(() => {
    const init = {};
    for (const f of tool.fields) init[f.name] = f.type === 'select' ? (f.options?.[0]?.value || '') : '';
    return init;
  });
  const [generating, setGenerating] = useState(false);
  const [output, setOutput] = useState('');
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const Icon = ICON[tool.icon] || Sparkles;

  const requiredMissing = useMemo(() => {
    return tool.fields.filter(f => f.required && !String(formData[f.name] || '').trim()).map(f => f.label);
  }, [formData, tool.fields]);

  const update = (name, value) => setFormData(fd => ({ ...fd, [name]: value }));

  const generate = async () => {
    setError(null); setGenerating(true); setOutput('');
    try {
      if (demoMode) {
        // Demo mode returns a canned response after a small delay.
        await new Promise(r => setTimeout(r, 800));
        setOutput(buildDemoOutput(tool, formData));
        return;
      }
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not signed in');
      const r = await fetch('/api/studio-generate', {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ tool_key: tool.key, form_data: formData }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || `Failed (${r.status})`);
      setOutput(data.output || '');
    } catch (e) {
      setError(e.message);
    } finally { setGenerating(false); }
  };

  const copyOutput = async () => {
    try {
      await navigator.clipboard.writeText(output);
      setCopied(true);
      showToast?.('Copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Could not copy. Select the text manually.');
    }
  };

  return (
    <div>
      {/* Breadcrumb / back */}
      <button onClick={onBack} style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        background: 'transparent', border: 'none', cursor: 'pointer',
        color: theme.colors.textLight, fontSize: 12, fontWeight: 600,
        padding: 0, marginBottom: 12,
      }}>
        <ArrowLeft size={13} /> All tools
      </button>

      {/* Tool header */}
      <div style={{
        display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16,
        padding: 14, background: theme.colors.card, borderRadius: 12,
        border: `1px solid ${theme.colors.border}`,
      }}>
        <div style={{
          width: 42, height: 42, borderRadius: 10,
          background: `${theme.colors.primary}15`, color: theme.colors.primary,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Icon size={20} />
        </div>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: theme.colors.text }}>{tool.label}</div>
          <div style={{ fontSize: 12, color: theme.colors.textLight, marginTop: 2 }}>{tool.blurb}</div>
        </div>
      </div>

      {/* Form */}
      <div style={{
        background: theme.colors.card, borderRadius: 12,
        border: `1px solid ${theme.colors.border}`, padding: 18,
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: 12,
      }}>
        {tool.fields.map(f => (
          <div key={f.name} style={{
            gridColumn: (f.type === 'textarea' ? '1 / -1' : 'auto'),
          }}>
            <label style={lbl()}>
              {f.label}{f.required && <span style={{ color: theme.colors.danger }}> *</span>}
            </label>
            {f.type === 'textarea' ? (
              <textarea
                value={formData[f.name] || ''}
                onChange={e => update(f.name, e.target.value)}
                placeholder={f.placeholder}
                rows={f.rows || 3}
                style={{ ...field(), resize: 'vertical', fontFamily: 'inherit' }}
              />
            ) : f.type === 'select' ? (
              <select
                value={formData[f.name] || ''}
                onChange={e => update(f.name, e.target.value)}
                style={field()}
              >
                {(f.options || []).map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            ) : (
              <input
                type={f.type || 'text'}
                value={formData[f.name] || ''}
                onChange={e => update(f.name, e.target.value)}
                placeholder={f.placeholder}
                style={field()}
              />
            )}
          </div>
        ))}

        <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginTop: 4 }}>
          <div style={{ fontSize: 11, color: theme.colors.textLight }}>
            {requiredMissing.length
              ? <>Missing: <strong>{requiredMissing.join(', ')}</strong></>
              : 'All set. Generate to see the output.'}
          </div>
          <button
            onClick={generate}
            disabled={generating || requiredMissing.length > 0}
            style={{
              padding: '10px 18px', borderRadius: 10, border: 'none',
              background: theme.colors.primary, color: 'white',
              fontSize: 13, fontWeight: 700, cursor: generating ? 'wait' : 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 6,
              opacity: (generating || requiredMissing.length > 0) ? 0.6 : 1,
            }}
          >
            {generating ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Sparkles size={14} />}
            {generating ? 'Generating…' : output ? 'Regenerate' : 'Generate'}
          </button>
        </div>
      </div>

      {error && (
        <div style={{
          marginTop: 12, padding: 12, background: '#FEF2F2', color: theme.colors.danger,
          border: `1px solid ${theme.colors.danger}30`, borderRadius: 10, fontSize: 13,
        }}>{error}</div>
      )}

      {/* Output */}
      {output && (
        <div style={{
          marginTop: 16, background: theme.colors.card,
          border: `1px solid ${theme.colors.border}`, borderRadius: 12, overflow: 'hidden',
        }}>
          <div style={{
            padding: '10px 14px', borderBottom: `1px solid ${theme.colors.border}`,
            background: '#F8FAFC', display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <CheckCircle2 size={14} color={theme.colors.success} />
            <div style={{ fontSize: 12, fontWeight: 700, color: theme.colors.text, flex: 1 }}>Draft</div>
            <button onClick={copyOutput} style={smallBtn(copied ? 'success' : 'primary')}>
              {copied ? <><CheckCircle2 size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
            </button>
            <button onClick={() => setOutput('')} style={smallBtn('ghost')}>
              <X size={12} /> Clear
            </button>
          </div>
          <pre style={{
            margin: 0, padding: 18, fontSize: 13, color: theme.colors.text,
            whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'inherit',
            lineHeight: 1.6,
          }}>{output}</pre>
        </div>
      )}

      <style>{`@keyframes spin { from{transform:rotate(0)} to{transform:rotate(360deg)} }`}</style>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Small helpers / styles
// ──────────────────────────────────────────────────────────────────
function lbl() {
  return {
    display: 'block', fontSize: 11, fontWeight: 700,
    color: theme.colors.textLight, textTransform: 'uppercase',
    letterSpacing: '0.06em', marginBottom: 5,
  };
}
function field() {
  return {
    width: '100%', boxSizing: 'border-box',
    padding: '9px 11px', borderRadius: 8,
    border: `1px solid ${theme.colors.border}`,
    fontSize: 13, color: theme.colors.text, background: theme.colors.card,
  };
}
function smallBtn(variant = 'primary') {
  const base = {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    border: 'none', borderRadius: 6, padding: '4px 9px',
    fontSize: 11, fontWeight: 600, cursor: 'pointer',
  };
  if (variant === 'success') return { ...base, background: theme.colors.success, color: 'white' };
  if (variant === 'ghost')   return { ...base, background: 'transparent', color: theme.colors.textLight };
  return { ...base, background: theme.colors.primary, color: 'white' };
}

function buildDemoOutput(tool, f) {
  const addr = f.address || f.address_past || 'your property';
  const client = f.client_name || f.client_first_name || f.client_name_past || f.client_name_check || f.client_name_ref || f.buyer_names || 'your client';
  return `[Demo output — hooking up real AI in production]\n\n`
    + `Tool: ${tool.label}\n`
    + `Subject: ${addr}\n`
    + `For: ${client}\n\n`
    + `Sign in with a real Trovva account to see Claude-generated copy for this tool.`;
}
